/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, forwardRef, useMemo } from 'react';
import { Box, Flex, useToast, Button, useDisclosure, Badge, SimpleGrid, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { faFileAlt, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import ExcelLife from '../Excel/ExcelLife';
import SpreadSheetTable from './table';
import SpreadSheetKeyValuePair from '../../Miscellaneous/spreadsheet-key-value-pair';
import EditMonthlyReportModal from '../../../Modal/EditMonthlyReportModal';
import Loading from '../../../Loading';
import CompanyName from '../../../CompanyName';
import LabelDate from '../../../LabelDate';
import c from '../../../../constant';
import useStore from '../../../../store';
import { mdMonthlyDueDate } from '../../../../utils/mdReportDueDate';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isAbleAllowEditing, isDataViewer } from '../../../../utils/market-development';
import { CompanyBadge } from '../../../Other';
import EditHistoryModal from '../../Miscellaneous/edit-history-modal';
import UserDislayNameById from '../../../UserDislayNameById';
import useMoment from '../../../../hooks/useMoment';

const Q_DETAIL = gql`
	query get($id: String!) {
		getMonthlyClaimReportById(id: $id) {
			id
			reportType
			month
			year
			createdAt
			updatedAt
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			isEditing
			editingUpdatedAt
			editingUpdatedBy
			editingDueDate
			editingDescription
			penalties {
				id
				active
			}
			claimItems {
				claimProducts {
					productLine {
						id
						nameKh
						nameEn
						type
					}
					productValues {
						id
						claimType
						claimValueType
						value
					}
				}
			}
		}
	}
`;

const Page = forwardRef(({ id = null }, ref) => {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { toDateString, toMonth, toYear } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [month, setMonth] = useState(null);
	const [year, setYear] = useState(null);
	const [isEditing, setEditing] = useState(false);
	const [createdAt, setCreatedAt] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const [isPenalized, setPenalized] = useState(false);
	const [company, setCompany] = useState(null);
	const [claimItem, setClaimItem] = useState(null);
	const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
	const [getDetail, { data: dataGet, loading: loadingGet, error: errorGet }] = useLazyQuery(Q_DETAIL, { fetchPolicy: 'no-cache' });

	useEffect(loadDetail, [id]);

	useEffect(() => {
		if (dataGet?.getMonthlyClaimReportById) {
			setMonth(parseInt(dataGet?.getMonthlyClaimReportById?.month));
			setYear(parseInt(dataGet?.getMonthlyClaimReportById?.year));
			setEditing(dataGet?.getMonthlyClaimReportById?.isEditing);
			setCreatedAt(dataGet?.getMonthlyClaimReportById?.createdAt);
			setUpdatedAt(dataGet?.getMonthlyClaimReportById?.updatedAt);
			setPenalized(dataGet?.getMonthlyClaimReportById?.penalties?.length > 0);
			setCompany(dataGet?.getMonthlyClaimReportById?.company);
			setClaimItem(dataGet?.getMonthlyClaimReportById?.claimItems[0]);
		}
	}, [dataGet]);

	useEffect(() => {
		if (errorGet) {
			if (errorGet?.graphQLErrors?.length > 0) {
				errorGet?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorGet?.message ? errorGet?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorGet]);

	function loadDetail() {
		id && getDetail({ variables: { id } });
	}

	const dueDate = useMemo(() => mdMonthlyDueDate({ year, month }), [year, month]);
	const isDueDateReached = useMemo(() => (dueDate ? moment().isSameOrAfter(dueDate, 'second') : false), [dueDate]);
	const canViewData = useMemo(() => isDataViewer({ user, companyType: 'Life' }), [user]);
	const canAllowEditing = useMemo(() => canViewData && isAbleAllowEditing({ user, companyType: 'Life' }), [user]);

	return (
		<>
			{loadingGet ? (
				<>
					<Loading />
				</>
			) : (
				<>
					<Flex pl={4} pr={4}>
						<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4}>
							<Flex w="100%" direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
								<>
									{isPenalized && (
										<Box>
											<Badge colorScheme="red" variant="solid">
												{t('Penalized')}
											</Badge>
										</Box>
									)}
								</>
								<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={company} />} />
								<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={company?.type} />} />
								<SpreadSheetKeyValuePair label="Month" value={toMonth(month)} />
								<SpreadSheetKeyValuePair label="Year" value={toYear(year)} />
								<SpreadSheetKeyValuePair label="Submitted At" value={<LabelDate input={createdAt} outputFormat={c.dateTimeFormat} />} />
								<>
									{updatedAt && (
										<>
											<SpreadSheetKeyValuePair
												label="Updated at"
												value={<LabelDate input={updatedAt} outputFormat={c.dateTimeFormat} />}
											/>
										</>
									)}
								</>
								{isDueDateReached && canViewData && isEditing && (
									<Flex direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
										<SpreadSheetKeyValuePair
											label="Allow Editing"
											value={<LabelDate input={dataGet?.getMonthlyClaimReportById?.editingUpdatedAt} outputFormat={c.dateTimeFormat} />}
										/>
										<SpreadSheetKeyValuePair
											label="By"
											value={<UserDislayNameById id={dataGet?.getMonthlyClaimReportById?.editingUpdatedBy} />}
										/>
										<SpreadSheetKeyValuePair
											label="Due Date"
											value={<LabelDate input={dataGet?.getMonthlyClaimReportById?.editingDueDate} outputFormat={c.dateTimeFormat} />}
										/>
										<SpreadSheetKeyValuePair label="Description" value={dataGet?.getMonthlyClaimReportById?.editingDescription} />
										{canAllowEditing && (
											<>
												<Box mt={2}>
													<Button
														onMouseDown={onOpen}
														isLoading={false}
														isDisabled={false}
														onClick={() => null}
														leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
														colorScheme="red"
														variant="solid"
														size="sm"
														borderRadius={c.borderRadius}
														border="1px solid #bdc3c7"
														mr={2}
														width={lang === 'kh' ? '219px' : '195px'}
													>
														{t('Stop Allowing Editing')}
													</Button>
												</Box>
											</>
										)}
									</Flex>
								)}
								<Flex mt={2}>
									{isDueDateReached && canAllowEditing && !isEditing && (
										<Button
											onMouseDown={onOpen}
											isLoading={false}
											isDisabled={false}
											onClick={() => null}
											leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Allow Editing')}
										</Button>
									)}
									{claimItem && canViewData && <ExcelLife month={month} year={year} numColumns={10} claimItem={claimItem} />}
									{canViewData && (
										<Flex ml={2}>
											<Button
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												leftIcon={<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 16 }} />}
												onClick={onHistoryOpen}
											>
												{t('View Logs')}
											</Button>
										</Flex>
									)}
								</Flex>
								{!isDueDateReached && canViewData && dueDate && (
									<Box h="100%" mt={2}>
										<Text fontSize="15px" fontStyle="italic" fontWeight="bold" justifyContent="center" color="orange.500">
											{t('Report can still be edited until the due date:')}&nbsp;
											{toDateString(dueDate, c.dateTimeFormat)}
										</Text>
									</Box>
								)}
							</Flex>
						</SimpleGrid>
					</Flex>
					<Box pl={4} pr={4} pb={4} pt={4} overflow="clip" hidden={canViewData ? false : true}>
						{claimItem && <SpreadSheetTable claimProducts={claimItem?.claimProducts} />}
					</Box>
				</>
			)}
			{isOpen && <EditMonthlyReportModal id={id} type="Claim" isEditable={isEditing} onClose={onClose} onCallback={loadDetail} />}
			{isHistoryOpen && <EditHistoryModal id={id} period="Monthly" category="Claim" isOpen={isHistoryOpen} onClose={onHistoryClose} />}
		</>
	);
});

export default Page;
